import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Card,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    CircularProgress,
    Pagination,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    CheckCircle as CompletedIcon,
    Drafts as DraftIcon,
    CloudOff as CloudOffIcon,
    CloudDone as CloudDoneIcon,
    Refresh as RefreshIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import tempExhibitBService from '../../services/tempExhibitBService';

const TempExhibitBList = () => {
    const navigate = useNavigate();
    const [forms, setForms] = useState([]);
    const [loading, setLoading] = useState(true);
    // Search removed as requested
    const [filterStatus, setFilterStatus] = useState('all');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, form: null });
    const [retryingForms, setRetryingForms] = useState(new Set());
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => {
        loadForms();
    }, []);

    const loadForms = async () => {
        setLoading(true);
        try {
            const allForms = await tempExhibitBService.getAllForms();
            setForms(allForms);
        } catch (error) {
            console.error('Error loading forms:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter forms based on status only
    const filteredForms = useMemo(() => {
        let filtered = [...forms];
        
        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(form => form.status === filterStatus);
        }
        
        return filtered;
    }, [forms, filterStatus]);

    // Count forms by status
    const statusCounts = useMemo(() => {
        return {
            all: forms.length,
            draft: forms.filter(f => f.status === 'draft').length,
            completed: forms.filter(f => f.status === 'completed').length
        };
    }, [forms]);

    // Pagination calculations
    const paginatedForms = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredForms.slice(startIndex, endIndex);
    }, [filteredForms, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredForms.length / itemsPerPage);

    // Reset to page 1 when filter changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filterStatus]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const handleDelete = async () => {
        if (!deleteDialog.form) return;
        
        try {
            await tempExhibitBService.deleteForm(deleteDialog.form.id);
            await loadForms();
        } catch (error) {
            console.error('Error deleting form:', error);
        } finally {
            setDeleteDialog({ open: false, form: null });
        }
    };

    const handleRetry = async (formId) => {
        setRetryingForms(prev => new Set([...prev, formId]));
        
        try {
            const result = await tempExhibitBService.retrySubmit(formId);
            if (result.success) {
                // Reload forms to show updated status
                await loadForms();
            } else {
                console.error('Retry failed:', result.error);
                // Could show error toast here
            }
        } catch (error) {
            console.error('Error retrying form submission:', error);
        } finally {
            setRetryingForms(prev => {
                const newSet = new Set(prev);
                newSet.delete(formId);
                return newSet;
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
        } catch {
            return '-';
        }
    };

    const getStatusChip = (status) => {
        if (status === 'completed') {
            return (
                <Chip 
                    icon={<CompletedIcon />} 
                    label="Completed" 
                    size="small"
                    sx={{ 
                        backgroundColor: '#4caf50',
                        color: 'white'
                    }}
                />
            );
        }
        return (
            <Chip 
                icon={<DraftIcon />} 
                label="Draft" 
                size="small"
                sx={{ 
                    backgroundColor: '#9e9e9e',
                    color: 'white'
                }}
            />
        );
    };

    const getSyncStatusChip = (form) => {
        // Only show sync status for completed forms
        if (form.status !== 'completed') return null;
        
        const syncStatus = form.syncStatus || 'pending';
        
        switch (syncStatus) {
            case 'synced':
                return (
                    <Tooltip title="Synced to server">
                        <Chip 
                            icon={<CloudDoneIcon />} 
                            label="Synced" 
                            size="small"
                            sx={{ 
                                backgroundColor: '#2196f3',
                                color: 'white'
                            }}
                        />
                    </Tooltip>
                );
            case 'failed':
                return (
                    <Tooltip title={`Failed: ${form.syncError || 'Unknown error'}`}>
                        <Chip 
                            icon={<ErrorIcon />} 
                            label="Sync Failed" 
                            size="small"
                            sx={{ 
                                backgroundColor: '#f44336',
                                color: 'white'
                            }}
                        />
                    </Tooltip>
                );
            case 'retrying':
                return (
                    <Chip 
                        icon={<CircularProgress size={12} sx={{ color: 'white' }} />} 
                        label="Syncing..." 
                        size="small"
                        sx={{ 
                            backgroundColor: '#ff9800',
                            color: 'white'
                        }}
                    />
                );
            default: // 'pending'
                return (
                    <Tooltip title="Not synced to server">
                        <Chip 
                            icon={<CloudOffIcon />} 
                            label="Not Synced" 
                            size="small"
                            sx={{ 
                                backgroundColor: '#ff9800',
                                color: 'white'
                            }}
                        />
                    </Tooltip>
                );
        }
    };

    return (
        <Box sx={{ 
            height: 'calc(100vh - 60px)', // Account for main app header
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ 
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 1.5,
                flexShrink: 0
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    mb: 1.5
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="600">
                            Hold Harmless Forms
                        </Typography>
                    </Box>
                    <Button 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/temps/exhibit-b')}
                        sx={{ 
                            backgroundColor: '#4caf50',
                            '&:hover': { backgroundColor: '#45a049' }
                        }}
                    >
                        New Form
                    </Button>
                </Box>

                {/* Filter Buttons */}
                <ToggleButtonGroup
                    value={filterStatus}
                    exclusive
                    onChange={(e, newValue) => newValue && setFilterStatus(newValue)}
                    fullWidth
                    size="small"
                >
                    <ToggleButton value="all">
                        All ({statusCounts.all})
                    </ToggleButton>
                    <ToggleButton 
                        value="draft"
                        sx={{
                            '&.Mui-selected': {
                                backgroundColor: '#9e9e9e',
                                color: 'white',
                                '&:hover': { backgroundColor: '#9e9e9e' }
                            }
                        }}
                    >
                        Draft ({statusCounts.draft})
                    </ToggleButton>
                    <ToggleButton 
                        value="completed"
                        sx={{
                            '&.Mui-selected': {
                                backgroundColor: '#4caf50',
                                color: 'white',
                                '&:hover': { backgroundColor: '#4caf50' }
                            }
                        }}
                    >
                        Completed ({statusCounts.completed})
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Forms List */}
            <Box sx={{ 
                p: 1.5, 
                flexGrow: 1,
                overflowY: 'auto'
            }}>
                {filteredForms.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                            No forms found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Form" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedForms.map((form) => (
                            <Card key={form.id} sx={{ p: 1.5 }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                {form.employeeName || 'No Name'}
                                            </Typography>
                                            {getStatusChip(form.status)}
                                            {getSyncStatusChip(form)}
                                            <Chip 
                                                label={form.language === 'spanish' ? 'Español' : 'English'} 
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 20 }}
                                            />
                                        </Box>
                                        
                                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Agency:</strong> {form.agencyName || '-'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                <strong>Created:</strong> {formatDate(form.createdAt)}
                                            </Typography>
                                            {form.status === 'completed' && (
                                                <Typography variant="caption" color="text.secondary">
                                                    <strong>Signed:</strong> {formatDate(form.signedDate)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Box>
                                    
                                    <Stack direction="row" spacing={0.5}>
                                        {form.status === 'draft' && (
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/temps/exhibit-b/${form.id}`)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {form.status === 'completed' && (
                                            <>
                                                <Tooltip title="View">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`/temps/exhibit-b/${form.id}`)}
                                                        color="info"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {(!form.syncStatus || form.syncStatus !== 'synced') && (
                                                    <Tooltip title="Retry sync to server">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRetry(form.id)}
                                                            color="warning"
                                                            disabled={retryingForms.has(form.id)}
                                                        >
                                                            {retryingForms.has(form.id) ? 
                                                                <CircularProgress size={20} /> : 
                                                                <RefreshIcon />
                                                            }
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </>
                                        )}
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                onClick={() => setDeleteDialog({ open: true, form })}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Box>
                            </Card>
                        ))}
                    </Stack>
                    
                    {/* Pagination Controls */}
                    {filteredForms.length > itemsPerPage && (
                        <Box sx={{ 
                            mt: 2, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredForms.length)} of {filteredForms.length}
                                </Typography>
                                <FormControl size="small">
                                    <Select
                                        value={itemsPerPage}
                                        onChange={handleItemsPerPageChange}
                                        sx={{ minWidth: 80 }}
                                    >
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                        <MenuItem value={50}>50</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Pagination 
                                count={totalPages} 
                                page={currentPage} 
                                onChange={handlePageChange}
                                color="primary"
                                size="medium"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                    </>
                )}
            </Box>

            {/* Delete Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, form: null })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this form for {deleteDialog.form?.employeeName}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, form: null })}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TempExhibitBList;